import { useEffect } from 'react';

export const ROUTE_MAP = {
  '/': '/en',
  '/lovpakke-priser': '/en/mandatory-package-prices',
  '/teori-undervisning': '/en/theory-classes',
  '/opstart': '/en/start-dates',
  '/valg-af-koereskole': '/en/choosing-a-driving-school',
  '/om-os': '/en/about-us',
  '/betingelser': '/en/terms',
  '/anbefalinger': '/en/testimonials',
  '/links': '/en/links',
  '/kontakt': '/en/contact',
  '/403': '/en/403',
  '/404': '/en/404'
};

export const REVERSE_ROUTE_MAP = Object.entries(ROUTE_MAP).reduce((acc, [da, en]) => {
  acc[en] = da;
  return acc;
}, {});

export function getEquivalentRoute(pathname, targetLang) {
  const cleanPath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  
  if (targetLang === 'en') {
    if (cleanPath.startsWith('/en')) return cleanPath;
    return ROUTE_MAP[cleanPath] || '/en';
  }
  
  if (targetLang === 'da') {
    if (!cleanPath.startsWith('/en')) return cleanPath;
    return REVERSE_ROUTE_MAP[cleanPath] || '/';
  }
  
  return cleanPath;
}

export function useSeo({
  title,
  description,
  lang = 'da',
  pathname = '/',
  image = 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/imgi-3-logo-18.png'
}) {
  useEffect(() => {
    // 1. Set HTML lang
    document.documentElement.lang = lang;

    // 2. Set document title
    if (title) {
      document.title = title;
    }

    const baseUrl = 'https://borgskoereskole.dk';
    const cleanPath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
    const currentUrl = `${baseUrl}${cleanPath === '/' ? '' : cleanPath}`;

    const daPath = lang === 'da' ? cleanPath : (REVERSE_ROUTE_MAP[cleanPath] || '/');
    const enPath = lang === 'en' ? cleanPath : (ROUTE_MAP[cleanPath] || '/en');

    const daUrl = `${baseUrl}${daPath === '/' ? '' : daPath}`;
    const enUrl = `${baseUrl}${enPath}`;

    // Helper to update or create tag
    const setMetaTag = (selector, attr, val, content) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, val);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setLinkTag = (rel, href, extraAttrs = {}) => {
      let selector = `link[rel="${rel}"]`;
      if (extraAttrs.hreflang) {
        selector += `[hreflang="${extraAttrs.hreflang}"]`;
      }
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        Object.entries(extraAttrs).forEach(([k, v]) => element.setAttribute(k, v));
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Description & OpenGraph
    if (description) {
      setMetaTag('meta[name="description"]', 'name', 'description', description);
      setMetaTag('meta[property="og:description"]', 'property', 'og:description', description);
    }
    if (title) {
      setMetaTag('meta[property="og:title"]', 'property', 'og:title', title);
    }
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', currentUrl);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', image);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');

    // Canonical & Hreflangs
    setLinkTag('canonical', currentUrl);
    setLinkTag('alternate', daUrl, { hreflang: 'da' });
    setLinkTag('alternate', enUrl, { hreflang: 'en' });
    setLinkTag('alternate', daUrl, { hreflang: 'x-default' });

  }, [title, description, lang, pathname, image]);
}
